/* eslint no-process-env: 0 */

import Head from 'next/head';
import { useState } from 'react';
import Script from 'next/script';
import { logger } from '@/utils/logger';
import { env } from '@zopauk/nextjs-env';
import { Navbar } from '@/components/Navbar';
import { Open_Sans } from 'next/font/google';
import { Amplitude } from '@/components/Amplitude';
import { Honeycomb } from '@zopauk/libs-otel/client';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LoadingSection } from '@/components/LoadingSection';
import { AppGlobalStyles } from '@/components/AppGlobalStyles';
import App, { type AppContext, type AppProps } from 'next/app';
import { setMembershipApiConfig } from '@/utils/api/membership';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SessionExpiryModal } from '@/components/SessionExpiryModal';
import { GlobalStyles, ZopaFooter, Modal } from '@zopauk/react-components';
import { googleTagManagerRawScript } from '@zopauk/react-third-party-scripts';
import { type Environments, SessionProvider, type Session } from '@zopauk/nextjs-auth';
import { type DehydratedState, QueryClientProvider, HydrationBoundary, QueryClient } from '@tanstack/react-query';

setMembershipApiConfig();

const openSans = Open_Sans({
  weight: ['400', '600', '700', '800'],
  subsets: ['latin'],
  display: 'swap',
});

type PageProps = {
  dehydratedState?: DehydratedState;
  session?: Session | null;
};

const ZopaApp = ({ pageProps: { session, ...pageProps }, Component }: AppProps<PageProps>) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: Number.parseInt(env('NEXT_PUBLIC_REACT_QUERY_RETRIES') || '3', 10),
            refetchOnWindowFocus: false,
            staleTime: 60000, // 1 min
          },
        },
      }),
  );

  Modal.setAppElement('body');

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          {env('NEXT_PUBLIC_APP_ENV') === 'development' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
          <Head>
            <meta content="width=device-width, initial-scale=1, shrink-to-fit=no" name="viewport" />
            {env('NEXT_PUBLIC_APP_ENV') !== 'production' ? <meta content="noindex" name="robots" /> : null}
          </Head>
          <SessionProvider ExpiryModal={SessionExpiryModal} Loading={LoadingSection} session={session}>
            <div className={openSans.className}>
              <GlobalStyles />
              <AppGlobalStyles font={openSans} />
              <Modal.Styles />

              <Amplitude />

              <Navbar
                links={[
                  {
                    label: 'Dashboard',
                    href: '/',
                  },
                  {
                    href: `${env('NEXT_PUBLIC_WEBSITE_URL') || 'https://www.zopa.com'}/help`,
                    'data-automation': 'ZA.navbar.help-link',
                    label: 'Help',
                  },
                ]}
                routes={{
                  userProfile: '/profile',
                }}
              />

              <section style={{ position: 'relative' }}>
                <Component {...pageProps} />
              </section>

              <ZopaFooter baseUrl={env('NEXT_PUBLIC_WEBSITE_URL') || 'https://www.zopa.com'} />
            </div>
            <Script id="google-tag-manager-script" strategy="lazyOnload">
              {googleTagManagerRawScript()}
            </Script>
            {['production', 'uat'].includes(env('NEXT_PUBLIC_APP_ENV') || '') ? (
              <Honeycomb
                serviceVersion={process.env.NEXT_PUBLIC_APP_VERSION || 'development'}
                environment={(env('NEXT_PUBLIC_APP_ENV') as Environments) || ''}
                token={env('NEXT_PUBLIC_HONEYCOMB_API_KEY') || ''}
                // libs-otel sends spans via a custom OTLP exporter (with the token as a Bearer
                // header) and a custom sampler, so the Honeycomb SDK's own apiKey/sampler checks
                // are false alarms. Skip them to keep the browser console clean. See WEBE-24749.
                honeycombOptions={{ skipOptionsValidation: true }}
                serviceName="zeos-hub"
                logger={logger}
              />
            ) : null}
          </SessionProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

ZopaApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};

export default ZopaApp;
